import {createSlice} from '@reduxjs/toolkit'
import AuthService from '../../api/auth'
import {toast} from 'react-toastify'
import FormHelper from '../../utils/forms'

const STEPS = {
    IDLE: "idle",
    LOADING: "loading",
    QR: "qr",
    ENABLING: "enabling",
    DISABLING: "disabling",
    RECOVERY_CODES: "recovery_codes",
    REGENERATING_CODES: "regenerating_codes"
}

export const mfaSlice = createSlice({
    name: 'mfa',
    initialState: {
        status: {enabled: false, types: []},
        step: STEPS.IDLE,
        qrData: {secret: "", otpauthUri: ""},
        recoveryCodes: []
    },
    reducers: {
        setStatus: (state, {payload}) => {
            state.status = payload
        },
        setStep: (state, {payload}) => {
            state.step = payload
        },
        setQrData: (state, {payload}) => {
            state.qrData = payload
        },
        setRecoveryCodes: (state, {payload}) => {
            state.recoveryCodes = payload
        },
        resetState: (state) => {
            state.step = STEPS.IDLE
            state.qrData = {secret: "", otpauthUri: ""}
            state.recoveryCodes = []
        }
    }
})

export const {
    setStatus,
    setStep,
    setQrData,
    setRecoveryCodes,
    resetState
} = mfaSlice.actions

export {STEPS as MFA_STEPS}

export default mfaSlice.reducer

export const getMFAStatus = () => {
    return async (dispatch) => {
        try {
            const {data} = await AuthService.getMFAStatus()
            dispatch(setStatus(data?.data || {enabled: false, types: []}))
        } catch (e) {
            dispatch(setStatus({enabled: false, types: []}))
        }
    }
}

export const enableMFA = (type = "totp") => {
    return async (dispatch) => {
        dispatch(setStep(STEPS.LOADING))
        try {
            const {data} = await AuthService.updateMFAStatus({enabled: true, type})
            if (data?.data) {
                dispatch(setQrData({
                    secret: data.data.secret,
                    otpauthUri: data.data.otpauth_uri
                }))
                dispatch(setStep(STEPS.QR))
            } else {
                toast.error("Failed to enable MFA")
                dispatch(setStep(STEPS.IDLE))
            }
        } catch (e) {
            toast.error(e?.response?.data?.message || "Failed to enable MFA")
            dispatch(setStep(STEPS.IDLE))
        }
    }
}

export const verifyMFA = (otpCode, setFieldError, type = "totp") => {
    return async (dispatch) => {
        dispatch(setStep(STEPS.ENABLING))
        try {
            const {data} = await AuthService.verifyMFA({otp_code: otpCode, type})
            dispatch(setStatus({enabled: true, types: [type]}))
            const codes = data?.data?.recovery_codes || []
            if (codes.length) {
                dispatch(setRecoveryCodes(codes))
                dispatch(setStep(STEPS.RECOVERY_CODES))
            } else {
                dispatch(resetState())
            }
            toast.success("Two-factor authentication enabled")
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
            dispatch(setStep(STEPS.QR))
        }
    }
}

export const regenerateRecoveryCodes = () => {
    return async (dispatch) => {
        dispatch(setStep(STEPS.REGENERATING_CODES))
        try {
            const {data} = await AuthService.regenerateRecoveryCodes()
            const codes = data?.data?.recovery_codes || []
            if (codes.length) {
                dispatch(setRecoveryCodes(codes))
                dispatch(setStep(STEPS.RECOVERY_CODES))
            } else {
                toast.error("Failed to generate recovery codes")
                dispatch(setStep(STEPS.IDLE))
            }
        } catch (e) {
            toast.error(e?.response?.data?.message || "Failed to generate recovery codes")
            dispatch(setStep(STEPS.RECOVERY_CODES))
        }
    }
}

export const disableMFA = (type = "totp") => {
    return async (dispatch) => {
        dispatch(setStep(STEPS.DISABLING))
        try {
            await AuthService.updateMFAStatus({enabled: false, type})
            dispatch(setStatus({enabled: false, types: []}))
            dispatch(setStep(STEPS.IDLE))
            toast.success("Two-factor authentication disabled")
        } catch (e) {
            toast.error(e?.response?.data?.message || "Failed to disable MFA")
            dispatch(setStep(STEPS.IDLE))
        }
    }
}
